import { cloneDeep, isArray, isNil } from 'lodash';
import { RolePermission } from '../../../../../api/graphql/generated';
import { isNumericRolePermissionUserdata } from '../../../../../components/role/role.utils';
import { Waypoint } from '../../../../../util/machine-variable.utils';
import {
  FineTunedPointRequired,
  PartUtil,
} from '../../../../../util/part.util';
import { Pose } from '../../../../../util/pose.util';
import {
  FlowItem,
  FlowItemArrayType,
  FlowItemParameter,
  FlowItemParameterMetadata,
  FlowItemParameterRefAdjustmentMetadata,
  FlowItemSection,
  ParametersForm,
} from '../../../shared/model/flow.types';
import { FlowItemService } from '../../../shared/service/service.types';
import {
  instanceOfFlowItem,
  instanceOfFlowItemSectionParent,
} from '../../../shared/util/flow-item.util';
import {
  getParameterValue,
  getParameterValueFromForm,
  getParametersFormWaitForTags,
  setParameterValueOnForm,
} from '../../../shared/util/flow-parameter.util';
import { BeaconWeldingData } from '../model/beacon-welding-data';

const hasApproach = (item: FlowItem): boolean => {
  const parameter: FlowItemParameter<boolean> | undefined =
    item.parameters.find(
      (p) => p.ref === BeaconWeldingData.FlowItemParameterRef.APPROACH
    );
  if (parameter) {
    return parameter.value;
  }

  return false;
};

const hasDepart = (item: FlowItem): boolean => {
  const parameter: FlowItemParameter<boolean> | undefined =
    item.parameters.find(
      (p) => p.ref === BeaconWeldingData.FlowItemParameterRef.DEPART
    );
  if (parameter) {
    return parameter.value;
  }

  return false;
};

const isSectionType = (flowItem?: FlowItem): boolean => {
  return flowItem?.ref === BeaconWeldingData.FlowItemRef.SECTION;
};

const isWeldType = (
  flowItem: FlowItem,
  type?: BeaconWeldingData.WeldTypeEnum
): boolean => {
  if (flowItem === undefined) {
    return false;
  } else {
    if (flowItem.ref !== BeaconWeldingData.FlowItemRef.WELD) {
      return false;
    } else if (type === undefined) {
      return true;
    }
    const idx = flowItem.parameters.findIndex(
      (p) =>
        p.ref === BeaconWeldingData.FlowItemParameterRef.WELD_TYPE &&
        p.value === type
    );

    return idx !== -1;
  }
};

const isSingleItemWeldFromForm = (
  form: ParametersForm | undefined
): boolean => {
  if (form) {
    const singleItemWelds = [
      BeaconWeldingData.WeldTypeEnum.TACK,
      BeaconWeldingData.WeldTypeEnum.ROTARY,
    ];

    const weldType = getParameterValueFromForm<BeaconWeldingData.WeldTypeEnum>(
      form,
      BeaconWeldingData.FlowItemParameterRef.WELD_TYPE
    );

    if (weldType) {
      return singleItemWelds.includes(weldType);
    }
  }

  return false;
};

const getWeldingParameterMetadataFromForm = (
  flowItemService: FlowItemService<BeaconWeldingData.FlowConfig>,
  form: ParametersForm,
  options: BeaconWeldingData.GetParameterMetadataFromFormOptions = {}
): FlowItemParameterMetadata[] => {
  const {
    forceDepart,
    forceApproach,
    forceIncludeTags,
    forceExcludeRefs,
    forceWeldType,
  } = options;

  const formClone = cloneDeep(form);

  let tags: BeaconWeldingData.FlowItemParameterTag[] = [];
  let excludedParams = [...(forceExcludeRefs ?? [])];

  const isDepart =
    forceDepart !== undefined
      ? forceDepart
      : getParameterValueFromForm<boolean>(
          form,
          BeaconWeldingData.FlowItemParameterRef.DEPART
        );

  const isApproach =
    forceApproach !== undefined
      ? forceApproach
      : getParameterValueFromForm<boolean>(
          form,
          BeaconWeldingData.FlowItemParameterRef.APPROACH
        );

  setParameterValueOnForm({
    form: formClone,
    ref: BeaconWeldingData.FlowItemParameterRef.APPROACH,
    value: isApproach,
    updateBaseValue: true,
  });

  setParameterValueOnForm({
    form: formClone,
    ref: BeaconWeldingData.FlowItemParameterRef.DEPART,
    value: isDepart,
    updateBaseValue: true,
  });

  const weldType =
    forceWeldType !== undefined
      ? forceWeldType
      : getParameterValueFromForm(
          form,
          BeaconWeldingData.FlowItemParameterRef.WELD_TYPE
        );

  const singleItemWeld = isSingleItemWeldFromForm(form);

  const isTackWeld = weldType === BeaconWeldingData.WeldTypeEnum.TACK;

  const isRotaryWeld = weldType === BeaconWeldingData.WeldTypeEnum.ROTARY;

  const process = getParameterValueFromForm(
    formClone,
    BeaconWeldingData.FlowItemParameterRef.PROCESS
  ) as BeaconWeldingData.WeldProcess;

  if (isDepart || singleItemWeld) {
    tags.push(BeaconWeldingData.FlowItemParameterTag.DEPART);
  }

  if (isApproach || singleItemWeld) {
    tags.push(BeaconWeldingData.FlowItemParameterTag.APPROACH);
  }

  if (isRotaryWeld) {
    tags.push(BeaconWeldingData.FlowItemParameterTag.ROTARY);
    tags.push(
      ...(getParametersFormWaitForTags(
        form
      ) as BeaconWeldingData.FlowItemParameterTag[])
    );
  }

  if (isTackWeld) {
    tags.push(BeaconWeldingData.FlowItemParameterTag.TACK);
    tags.push(process as unknown as BeaconWeldingData.FlowItemParameterTag);
  } else {
    if (!isDepart || isRotaryWeld) {
      tags.push(process as unknown as BeaconWeldingData.FlowItemParameterTag);

      if (weldType === BeaconWeldingData.WeldTypeEnum.LINEAR_STITCH) {
        tags.push(BeaconWeldingData.FlowItemParameterTag.STITCH);
      }

      const weaveEnabled = getParameterValueFromForm<boolean>(
        formClone,
        BeaconWeldingData.FlowItemParameterRef.WEAVE_ENABLED
      );

      if (weaveEnabled) {
        tags.push(BeaconWeldingData.FlowItemParameterTag.WEAVE);
      }
    }
  }

  if (!isDepart || singleItemWeld) {
    const synergicMode = getParameterValueFromForm<boolean>(
      formClone,
      BeaconWeldingData.FlowItemParameterRef.SYNERGIC_MODE
    );

    if (synergicMode !== undefined && synergicMode === true) {
      tags.push(BeaconWeldingData.FlowItemParameterTag.SYNERGIC_MODE);
    }

    const useWireFeedSpeed = getParameterValueFromForm<boolean>(
      formClone,
      BeaconWeldingData.FlowItemParameterRef.USE_WIRE_FEED_SPEED
    );

    if (useWireFeedSpeed !== undefined && useWireFeedSpeed === true) {
      tags.push(BeaconWeldingData.FlowItemParameterTag.USE_WIRE_FEED_SPEED);
    }
  }

  if (forceIncludeTags) {
    tags = tags.concat(forceIncludeTags);
  }

  const uniqueTags = Array.from(new Set(tags));

  let params = flowItemService.getParametersMetadataByTags(
    BeaconWeldingData.FlowItemRef.WELD,
    formClone,
    uniqueTags,
    true
  );

  if (isRotaryWeld) {
    excludedParams = excludedParams.concat([
      BeaconWeldingData.FlowItemParameterRef.TRAVEL_SPEED,
    ]);
  } else if (isDepart || isTackWeld) {
    excludedParams = excludedParams.concat([
      BeaconWeldingData.FlowItemParameterRef.TRAVEL_SPEED,
      BeaconWeldingData.FlowItemParameterRef.WEAVE_ENABLED,
    ]);
  }

  params = params.filter(
    (param) =>
      !excludedParams.includes(
        param.ref as BeaconWeldingData.FlowItemParameterRef
      )
  );

  return params;
};

const getNextWeldIndex = (
  currentIndex: number,
  weldItems: FlowItem[]
): number | undefined => {
  let nextIndex: number | undefined = undefined;
  for (let i = currentIndex + 1; i < weldItems.length; i++) {
    const item = weldItems[i];
    if (isWeldType(item)) {
      nextIndex = i;
      break;
    }
  }
  return nextIndex;
};

/* for a given flowItem, this function returns an array of welds
  that make up the complete weld that the flowItem is part of */
const getContiguousWeldForFlowItem = (
  flowItem: FlowItem,
  flowItems: FlowItem[]
): FlowItem[] => {
  let weldItems: FlowItem[] = [];
  if (isWeldType(flowItem)) {
    const start = getStartOfWeldIndexFromFlowItem(flowItem, flowItems);
    const end = getEndOfWeldIndexFromFlowItem(flowItem, flowItems);
    if (start !== undefined && end !== undefined) {
      weldItems = flowItems.slice(start, end + 1);
    }
  }
  return weldItems;
};

const getStartOfWeldIndexFromFlowItem = (
  flowItem: FlowItem,
  flowItems: FlowItem[]
): number | undefined => {
  let startIndex: number | undefined = undefined;
  const flowItemIndex: number | undefined = flowItem.id
    ? flowItems.findIndex((i) => i.id !== undefined && i.id === flowItem.id)
    : undefined;
  if (flowItemIndex !== undefined && isWeldType(flowItem)) {
    if (hasApproach(flowItem)) {
      startIndex = flowItemIndex;
    } else {
      for (let i = flowItemIndex; i >= 0; i--) {
        const item = flowItems[i];
        if (!isSectionType(item) && isWeldType(item) && hasApproach(item)) {
          startIndex = i;
          break;
        }
      }
    }
  }
  return startIndex;
};

const getEndOfWeldIndexFromFlowItem = (
  flowItem: FlowItem,
  flowItems: FlowItem[]
): number | undefined => {
  let endIndex: number | undefined = undefined;
  const flowItemIndex: number | undefined = flowItem.id
    ? flowItems.findIndex((i) => i.id !== undefined && i.id === flowItem.id)
    : undefined;
  if (flowItemIndex !== undefined && isWeldType(flowItem)) {
    if (hasDepart(flowItem)) {
      endIndex = flowItemIndex;
    } else {
      for (let i = flowItemIndex; i <= flowItems.length; i++) {
        const item = flowItems[i];
        if (!isSectionType(item) && isWeldType(item) && hasDepart(item)) {
          endIndex = i;
          break;
        }
      }
    }
  }
  return endIndex;
};

interface FlowItemAngles {
  id?: string;
  travelAngleFrom?: number;
  travelAngleTo?: number;
}

const getPoseFromFlowItem = (flowItem?: FlowItem): Pose | undefined => {
  if (flowItem) {
    const wp: Waypoint | undefined = getParameterValue(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.WAYPOINT
    );
    if (wp) {
      return [wp.x, wp.y, wp.z, wp.rx, wp.ry, wp.rz];
    }
  }
  return;
};

const getWeldItemTorchAngles = (
  flowItem: FlowItem,
  flowItems: FlowItem[]
): FlowItemAngles | undefined => {
  const angles = getWeldingTorchAngles(flowItem, flowItems);
  const itemAngles = angles.find(
    (a) => a.id !== undefined && a.id === flowItem.id
  );
  return itemAngles;
};

const getWeldingTorchAngles = (
  flowItem: FlowItem,
  flowItems: FlowItem[]
): FlowItemAngles[] => {
  const flowItemAngles: FlowItemAngles[] = flowItems.map((fi) => ({
    id: fi.id,
  }));

  const weldItems = getContiguousWeldForFlowItem(flowItem, flowItems);

  for (let i = 0; i < weldItems.length; i++) {
    const isNotLastItems = i < weldItems.length - 1;
    const thisItem = weldItems[i];
    if (isNotLastItems) {
      const nextItemIndex = getNextWeldIndex(i, weldItems);
      let nextItem: FlowItem | undefined = undefined;
      if (nextItemIndex !== undefined) {
        nextItem = weldItems[nextItemIndex];
      }

      const isRadial = isWeldType(
        thisItem,
        BeaconWeldingData.WeldTypeEnum.RADIAL
      );
      const isLinear = isWeldType(
        thisItem,
        BeaconWeldingData.WeldTypeEnum.LINEAR
      );
      const isLinearStitch = isWeldType(
        thisItem,
        BeaconWeldingData.WeldTypeEnum.LINEAR_STITCH
      );
      const isLinearType = isLinear || isLinearStitch;

      const thisItemAngles = flowItemAngles[i] ?? {};
      let nextItemAngles: FlowItemAngles = { id: nextItem?.id };
      if (nextItemIndex !== undefined) {
        nextItemAngles = {
          ...nextItemAngles,
          ...flowItemAngles[nextItemIndex],
        };
      }

      const thisPose = getPoseFromFlowItem(thisItem);
      const nextPose = getPoseFromFlowItem(nextItem);
      if (thisPose && nextPose) {
        if (isLinearType) {
          if (thisItemAngles.travelAngleFrom === undefined) {
            thisItemAngles.travelAngleFrom =
              PartUtil.getAngleOfPerpToTravelAxis_linear(
                thisPose,
                nextPose,
                FineTunedPointRequired.START_POINT
              );
            if (nextItemAngles.travelAngleTo === undefined) {
              nextItemAngles.travelAngleTo =
                PartUtil.getAngleOfPerpToTravelAxis_linear(
                  thisPose,
                  nextPose,
                  FineTunedPointRequired.END_POINT
                );
            }
            // update flowItemAngles for linears here
            flowItemAngles[i] = { ...flowItemAngles[i], ...thisItemAngles };
            if (nextItemIndex) {
              flowItemAngles[nextItemIndex] = {
                ...flowItemAngles[nextItemIndex],
                ...nextItemAngles,
              };
            }
          }
        } else if (isRadial) {
          if (thisItemAngles.travelAngleFrom === undefined) {
            const secondNextItemIndex = getNextWeldIndex(i, weldItems);
            let secondNextItem: FlowItem | undefined = undefined;
            if (secondNextItemIndex !== undefined) {
              secondNextItem = weldItems[secondNextItemIndex];
            }
            let secondNextItemAngles: FlowItemAngles = {
              id: secondNextItem?.id,
            };
            if (secondNextItemIndex) {
              secondNextItemAngles = {
                ...secondNextItemAngles,
                ...flowItemAngles[secondNextItemIndex],
              };
            }

            const secondNextPose = getPoseFromFlowItem(secondNextItem);
            if (secondNextPose) {
              thisItemAngles.travelAngleFrom =
                PartUtil.getAngleOfPerpToTravelAxis_radial(
                  thisPose,
                  nextPose,
                  secondNextPose,
                  FineTunedPointRequired.START_POINT
                );
              if (nextItemAngles.travelAngleTo === undefined) {
                nextItemAngles.travelAngleTo =
                  PartUtil.getAngleOfPerpToTravelAxis_radial(
                    thisPose,
                    nextPose,
                    secondNextPose,
                    FineTunedPointRequired.MID_POINT
                  );
                // on a radial, the angle to the mid-point is the same as the angle from the midpoint
                nextItemAngles.travelAngleFrom = nextItemAngles.travelAngleTo;
                secondNextItemAngles.travelAngleTo =
                  PartUtil.getAngleOfPerpToTravelAxis_radial(
                    thisPose,
                    nextPose,
                    secondNextPose,
                    FineTunedPointRequired.END_POINT
                  );
              }
            }
            // update flowItemAngles for radials here
            flowItemAngles[i] = { ...flowItemAngles[i], ...thisItemAngles };
            if (nextItemIndex) {
              flowItemAngles[nextItemIndex] = {
                ...flowItemAngles[nextItemIndex],
                ...nextItemAngles,
              };
            }
            if (secondNextItemIndex) {
              flowItemAngles[secondNextItemIndex] = {
                ...flowItemAngles[secondNextItemIndex],
                ...secondNextItemAngles,
              };
            }
          }
        }
      }
    }
  }

  return flowItemAngles;
};

const instanceOfFlowItemMultipassSectionParent = (
  item: FlowItemArrayType
): item is BeaconWeldingData.FlowItemMultipassSectionParent => {
  return (
    instanceOfFlowItemSectionParent(item) &&
    item.attributes?.type === BeaconWeldingData.FlowSectionType.MULTIPASS
  );
};

const instanceOfFlowItemStitchSection = (
  item: FlowItemArrayType
): item is BeaconWeldingData.FlowItemStitchSection => {
  return (
    instanceOfFlowItem(item) &&
    item.ref === BeaconWeldingData.FlowItemRef.SECTION &&
    item.attributes?.type === BeaconWeldingData.FlowSectionType.STITCH
  );
};

const instanceOfFlowItemStitchSectionParent = (
  item: FlowItemArrayType
): item is BeaconWeldingData.FlowItemStitchSectionParent => {
  return (
    instanceOfFlowItemSectionParent(item) &&
    item.attributes?.type === BeaconWeldingData.FlowSectionType.STITCH
  );
};

const sectionBreaksSequence = (item: FlowItemSection): boolean => {
  return instanceOfFlowItemStitchSectionParent(item);
};

const buildFlowItemParameterAdjustmentMetadata = (params: {
  permissions: RolePermission[];
  equipment?: string;
}): BeaconWeldingData.AppFlowItemParameterRefAdjustmentMetadata => {
  const { permissions, equipment } = params;
  const adjustmentMetadata: FlowItemParameterRefAdjustmentMetadata = {};

  permissions.forEach((permission) => {
    const requiredEquipment =
      permission.rolePermissionTemplate.metadata.equipment;
    let hasEquipment = false;

    if (isNil(requiredEquipment)) {
      hasEquipment = true;
    } else if (isArray(requiredEquipment)) {
      hasEquipment = requiredEquipment.includes(equipment);
    } else {
      hasEquipment = requiredEquipment === equipment;
    }

    if (
      permission.appSlug === BeaconWeldingData.APP_SLUG &&
      permission.can &&
      isNumericRolePermissionUserdata(permission.userData) &&
      hasEquipment
    ) {
      const ref =
        permission.fieldApiName as BeaconWeldingData.FlowItemParameterRef;
      const value = permission.userData.rolePermissionInputValue;
      const type = permission.userData.rolePermissionInputType;

      adjustmentMetadata[ref] = { value, type };
    }
  });

  return adjustmentMetadata;
};

const convertToBeaconWeldingDataWelder = (
  welder: string
): BeaconWeldingData.Welder | undefined => {
  return Object.values(BeaconWeldingData.Welder).find(
    (value) => value === welder
  );
};

export {
  convertToBeaconWeldingDataWelder,
  getWeldItemTorchAngles,
  getWeldingParameterMetadataFromForm,
  isSingleItemWeldFromForm,
  isWeldType,
  instanceOfFlowItemMultipassSectionParent,
  instanceOfFlowItemStitchSection,
  instanceOfFlowItemStitchSectionParent,
  sectionBreaksSequence,
  buildFlowItemParameterAdjustmentMetadata,
};
